import React from "react";
import {
  calculateTotalToday,
  calculateTotal,
  getBudgetPace,
  calculatePercentThruBudget,
} from "../utils/budgetUtils";
import { calculatePercentThruMonth } from "../utils/calendarUtils";
import { useTheme } from "../contexts/ThemeContext";
import { useEntryAccent } from "../hooks/useEntryAccent";

const BudgetSummary = ({ budget, expensesThisMonth }) => {
  const { theme } = useTheme();
  const gradient = theme.colors.gradient;

  // budget is null until the profile loads, and may legitimately be 0.
  const monthlyBudget = Number(budget);
  const hasBudget = Number.isFinite(monthlyBudget) && monthlyBudget > 0;

  const spent = calculateTotal(expensesThisMonth);
  const pace = getBudgetPace(expensesThisMonth, budget);

  // Both are numbers, so the comparisons below are numeric. Formatting to a
  // string happens only at the point of rendering — comparing the formatted
  // strings would sort them lexicographically ("9" > "50").
  const percentMonth = calculatePercentThruMonth();
  const percentBudget = calculatePercentThruBudget(expensesThisMonth, budget);

  let budgetBarColor = "bg-emerald-400";
  if (percentBudget > 100) {
    budgetBarColor = "bg-red-400";
  } else if (percentBudget > percentMonth) {
    budgetBarColor = "bg-amber-400";
  }

  return (
    <div className={`bg-gradient-to-br ${gradient.bg} p-5 rounded-2xl shadow-soft text-white`}>
      {/* Main stat */}
      <div className="text-center mb-4">
        <p className={`${theme.colors.text.muted} text-sm font-medium uppercase tracking-wide`}>Today's Spending</p>
        <h2 className="text-4xl font-bold mt-1">
          ${calculateTotalToday(expensesThisMonth).toFixed(0)}
        </h2>
      </div>

      {/* Status badge */}
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-4 text-center">
        <p className="text-sm">
          {new Date().toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
          })}
          {" — "}
          {hasBudget ? (
            <span
              className={
                pace.isUnder
                  ? "text-emerald-300 font-semibold"
                  : "text-amber-300 font-semibold"
              }
            >
              ${pace.amount.toFixed(0)} {pace.isUnder ? "under" : "over"}
            </span>
          ) : (
            <span className="font-semibold">No budget set</span>
          )}
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
          <p className={`${theme.colors.text.muted} text-xs uppercase tracking-wide`}>Spent</p>
          <p className="text-xl font-bold">${spent.toFixed(0)}</p>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
          <p className={`${theme.colors.text.muted} text-xs uppercase tracking-wide`}>Remaining</p>
          <p className="text-xl font-bold">
            {hasBudget ? `$${(monthlyBudget - spent).toFixed(0)}` : "—"}
          </p>
        </div>
      </div>

      {/* Progress bars */}
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className={theme.colors.text.muted}>Month Progress</span>
            <span className="font-semibold">{percentMonth.toFixed(0)}%</span>
          </div>
          <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-2 bg-white rounded-full transition-all duration-500"
              style={{ width: `${Math.min(percentMonth, 100)}%` }}
            ></div>
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className={theme.colors.text.muted}>Budget Used</span>
            <span className="font-semibold">{percentBudget.toFixed(0)}%</span>
          </div>
          <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${budgetBarColor}`}
              style={{ width: `${Math.min(percentBudget, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Per-category breakdown for a month of entries. Shared by income and expenses.
 *
 * @param {Object} props
 * @param {Record<string, number>} props.totalsByCategory
 * @param {Object[]} props.entries
 * @param {import('../constants/entryKinds').EntryKind} props.kind
 */
const EntrySummary = ({ totalsByCategory, entries, kind }) => {
  const accent = useEntryAccent(kind);

  const total = calculateTotal(entries);
  const categoryEntries = Object.entries(totalsByCategory).sort(
    ([, a], [, b]) => b - a
  );

  return (
    <div className="bg-white rounded-2xl shadow-soft p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-gray-800 text-lg">Breakdown</h4>
        <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold">
          ${total.toFixed(2)}
        </div>
      </div>
      {categoryEntries.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-2">
          {kind.emptyBreakdownText}
        </p>
      ) : (
        <div className="space-y-2">
          {categoryEntries.map(([category, categoryTotal]) => {
            const percentage = total > 0 ? (categoryTotal / total) * 100 : 0;
            return (
              <div key={category} className="group">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700 font-medium">{category}</span>
                  <span className="text-gray-500">${categoryTotal.toFixed(2)}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-1.5 bg-gradient-to-r ${accent.button} rounded-full transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export { BudgetSummary, EntrySummary };
